import { WebConfig } from "../web-config";

/**
 * Format file size in bytes to human-readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  const units = ["bytes", "KB", "MB", "GB"];
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  const precision = value >= 10 || unitIndex === 0 ? 0 : 1;
  return `${value.toFixed(precision)} ${units[unitIndex]}`;
};

/**
 * Base URL for publicly-served uploaded files.
 * Comes from the environment variable REACT_APP_PUBLIC_FILE_BASE_URL.
 * Falls back to REACT_APP_API_URL + '/media' for backward-compat.
 */
const getPublicFileBaseUrl = () => {
  return (
    WebConfig.REACT_APP_PUBLIC_FILE_BASE_URL ||
    `${WebConfig.REACT_APP_API_URL || ""}/media`
  );
};
/**
 * Build the full public URL for a file attachment.
 *
 * The backend stores only a *relative* path in the DB (e.g.
 * "attachments/2026/02/uuid.png").  This function prepends the
 * PUBLIC_FILE_BASE_URL so the browser can fetch the file from
 * whichever server is physically serving them (Express dev server,
 * Nginx, IIS, CDN, etc.).
 *
 * It also handles legacy documents that still carry the old
 * `fileUrl` field (e.g. "/uploads/filename.png") by falling back
 * to the API base URL.
 *
 * @param {Object} file - File/attachment object from the API
 * @returns {string} Full public URL
 */
export const getFileUrl = (file) => {
  if (!file) return "";
  const PUBLIC_FILE_BASE_URL = getPublicFileBaseUrl();
  // ── New field: relative path stored by the disk-based storage system ──
  if (file.filePath) {
    // Strip any leading slash — filePath should be purely relative.
    const cleaned = file.filePath.replace(/^\/+/, "");
    const base = PUBLIC_FILE_BASE_URL.replace(/\/+$/, "");
    return `${base}/${cleaned}`;
  }

  // ── Legacy field: fileUrl (absolute or relative to API) ──
  if (file.fileUrl) {
    if (file.fileUrl.startsWith("http")) {
      return file.fileUrl;
    }
    const API_BASE_URL = WebConfig.REACT_APP_API_URL || "";
    return `${API_BASE_URL}${file.fileUrl.startsWith("/") ? "" : "/"}${file.fileUrl}`;
  }

  // ── Fallback: url field ──
  if (file.url) {
    if (file.url.startsWith("http")) {
      return file.url;
    }
    const API_BASE_URL = WebConfig.REACT_APP_API_URL || "";
    return `${API_BASE_URL}${file.url.startsWith("/") ? "" : "/"}${file.url}`;
  }

  return "";
};

/**
 * Get file name from file object
 * @param {Object} file - File object
 * @returns {string} File name
 */
export const getFileName = (file) => {
  if (!file) return "Attachment";
  return file.filename || file.originalName || file.name || "Attachment";
};

/**
 * Check if file is an image
 * @param {Object} file - File object
 * @returns {boolean} True if image
 */
export const isImageFile = (file) => {
  if (!file) return false;
  const fileType = (
    file.fileType ||
    file.mimeType ||
    file.type ||
    ""
  ).toLowerCase();
  return fileType.startsWith("image/");
};

/**
 * Check if file is a PDF
 * @param {Object} file - File object
 * @returns {boolean} True if PDF
 */
export const isPdfFile = (file) => {
  if (!file) return false;
  const fileType = (
    file.fileType ||
    file.mimeType ||
    file.type ||
    ""
  ).toLowerCase();
  return fileType === "application/pdf" || fileType === "pdf";
};

/**
 * Check if file is a Word document (DOC/DOCX)
 * @param {Object} file - File object
 * @returns {boolean} True if Word document
 */
export const isWordFile = (file) => {
  if (!file) return false;
  const fileType = (
    file.fileType ||
    file.mimeType ||
    file.type ||
    ""
  ).toLowerCase();
  return (
    fileType === "application/msword" ||
    fileType ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  );
};

/**
 * Check if file is an Excel spreadsheet (XLS/XLSX)
 * @param {Object} file - File object
 * @returns {boolean} True if Excel file
 */
export const isExcelFile = (file) => {
  if (!file) return false;
  const fileType = (
    file.fileType ||
    file.mimeType ||
    file.type ||
    ""
  ).toLowerCase();
  return (
    fileType === "application/vnd.ms-excel" ||
    fileType ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  );
};

/**
 * Check if file is an Outlook message (MSG)
 * @param {Object} file - File object
 * @returns {boolean} True if MSG file
 */
export const isMsgFile = (file) => {
  if (!file) return false;
  const fileType = (
    file.fileType ||
    file.mimeType ||
    file.type ||
    ""
  ).toLowerCase();
  return fileType === "application/vnd.ms-outlook";
};

/**
 * Check if file can be previewed inline in the browser
 * @param {Object} file - File object
 * @returns {boolean} True if previewable
 */
export const isPreviewableFile = (file) => {
  return isImageFile(file) || isPdfFile(file);
};
